import { HOME_PAGE } from '@/app/constants';
import { usePathname, useRouter } from '@/i18n/routing';

// 精确匹配路由配置
const EXACT_MATCH_ROUTES: Record<string, string> = {
  '/project': HOME_PAGE,
};

// 前缀匹配路由配置
const PREFIX_MATCH_ROUTES: Record<string, string> = {
  '/activity': HOME_PAGE,
  '/project': '/project',
};

/**
 * 根据当前路径获取默认的返回路径
 * @param currentPath 当前路径
 * @returns 默认返回路径
 */
const getDefaultBackPathname = (currentPath: string): string => {
  // 优先精确匹配
  if (EXACT_MATCH_ROUTES[currentPath]) {
    return EXACT_MATCH_ROUTES[currentPath];
  }

  // 再进行前缀匹配
  const prefixMatch = Object.entries(PREFIX_MATCH_ROUTES).find(([key]) => currentPath.startsWith(key));

  return prefixMatch?.[1] ?? '/';
};

/**
 * 检查是否可以安全地使用 router.back()
 * @returns 是否可以安全后退
 */
const canSafelyBack = () => {
  // 如果在服务器端，不能安全后退
  if (typeof window === 'undefined') return false;

  // 如果没有历史记录，不能后退
  if (window.history.length <= 1) return false;

  // 如果没有来源页面，不能后退
  const referrer = document.referrer;
  if (!referrer) return false;

  // 如果来源页面不在当前域名下，不能后退
  const currentHost = window.location.host;
  const referrerHost = new URL(referrer).host;
  if (currentHost !== referrerHost) return false;

  // 如果来源页面是当前页面的首页，则不可后退。因为谷歌登录后，这里document.referrer会是首页。但是router.back()会回退到登录页。
  const referrerPathname = new URL(referrer).pathname;
  if (referrerPathname === '/' || referrerPathname === '') return false;

  return true;
};

/**
 * 检查当前页是否是被 target="_blank" 在新标签页打开的
 * - window.opener 存在（同源新窗口才能拿到 opener）
 * - 或 history.length <= 1 且 referrer 来自同源（无法回退说明是新开的）
 */
const isOpenedInNewTab = () => {
  if (typeof window === 'undefined') return false;
  if (window.opener && !window.opener.closed) return true;
  if (window.history.length <= 1) {
    const referrer = document.referrer;
    if (!referrer) return false;
    try {
      const referrerHost = new URL(referrer).host;
      return referrerHost === window.location.host;
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * 返回上一页的 Hook
 * 在新标签页打开页面时，不使用浏览器返回，而是使用默认返回路径
 *
 * @param options.forceBrowserBack 强制使用浏览器原生 history.back() 返回，
 *   适用于明确知道当前页是同标签页跳转、需要严格回到上一历史记录的场景
 *   (例如：协议页里要求点返回时回到 login 页，而不是按规则跳到首页)
 */
export const useGoBack = (options: { forceBrowserBack?: boolean } = {}) => {
  const currentPathname = usePathname();
  const router = useRouter();
  return () => {
    if (options.forceBrowserBack && typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
      return;
    }

    const defaultBackPathname = getDefaultBackPathname(currentPathname);

    // 如果可以安全后退，则用浏览器默认的后退
    if (canSafelyBack()) {
      router.back();
      return;
    }

    // 如果是新标签页打开的（无可返回历史），尝试关闭当前标签页
    if (isOpenedInNewTab()) {
      try {
        window.close();
      } catch {
        // 忽略：部分浏览器禁止脚本关闭非脚本打开的窗口
      }
      // window.close() 可能被浏览器拦截，留作兜底跳转
      // 加 setTimeout 让 close 先执行；若拦截才走 push
      setTimeout(() => {
        if (!window.closed) {
          router.push(defaultBackPathname);
        }
      }, 50);
      return;
    }

    router.push(defaultBackPathname);
  };
};
